import React, { useEffect, useState } from "react";
import { isDirty, getFormValues, destroy, initialize, getFormInitialValues } from "redux-form";

import { isEqual } from "lodash";

import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";
import { useDispatch, useSelector } from "react-redux";

import { VersionDetails, updateVersionVariables } from "reducers/VersionDetails";
import { fetchSingleVersion, loadCreativeVersions } from "reducers/CreativeVersions";
import EditVersion from "./EditVersion";
import Toolbar from "./Toolbar";
import SaveAsModal from "./SaveAsModal";
import { sanitizeVariableFieldName } from "./Helpers";
import { clearCropMetadata } from "reducers/Refinements";

import useSearchCache from "hooks/useSearchCache";
import useConfirmClose from "hooks/useConfirmClose";
import useFramingCheck from "hooks/useFramingCheck";

import EditVersionPreview from "./EditVersionPreview";
import InlineVersionPreview from './InlineVersionPreview';

import { isAfterEffectsCreative, isMaxDynamicSocialVersions, useModalStack } from "utils/common";

import { ASSET_TYPES } from "utils/constants";
import { serializeVariables, getVariableList } from "utils/VersionHelpers";
import { EDIT_VERSION_BLADE_FORM } from "forms/list";

import "./EditVersionBlade.css";

function pristineSelector(state) {
	/*
		This function is used to check if the form is pristine, to see if we need to confirm before closing.
		We have null change events that happen in TextEditor for some reason, which prevent us from
		getting accurate metadata from the `isDirty`/`isPristine` redux-form selectors.

		There are also values added like `ctaFormatted` for `cta` fields by TextEditor, so we can't
		just do a full comparison between `values` and `initialValues`.

		Instead, we go key by key through `initialValues` and check that the corresponding value
		in `values` matches it.
	*/

	const values = getFormValues(EDIT_VERSION_BLADE_FORM)(state) || {};
	const initialValues = getFormInitialValues(EDIT_VERSION_BLADE_FORM)(state) || {};

	return Object.keys(initialValues).every(key => isEqual(values[key], initialValues[key]));
}

export function mapAssetToOption(asset) {
	/*
		Text assets also appear with their ({id}) in the dropdown.
	*/

	let label = asset.name;
	if (asset.assetType === ASSET_TYPES.TEXT) {
		label = `${label} (${asset.id})`;
	}

	return {
		label,
		data: asset.name,
		assetId: asset.id,
		filesize: asset.filesize,
		type: asset.assetType,
	};
}

export function getAssetOptionsFromPayload(response) {
	return response?.payload?.data?.items?.map(mapAssetToOption) || [];
}

export function initializeEditVersionValues(variables = []) {
	return variables.reduce((obj, variable = {}) => {
		const fieldName = sanitizeVariableFieldName(variable.name);
		return {
			...obj,
			[fieldName]: variable,
		};
	}, {});
}

export default function EditVersionBlade(props) {
	let { onClose, libraryId, creativeId, versionId } = props
		, dispatch = useDispatch()
		, hasEdits = useSelector(isDirty(EDIT_VERSION_BLADE_FORM))
		, formValues = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM))
		, initialValues =  useSelector(getFormInitialValues(EDIT_VERSION_BLADE_FORM)) || {}
		, { loadVersionDetails, clearVersionDetails, setVariableSearchTerm } = VersionDetails
		, [isFullScreen, setIsFullScreen] = useState(false)
		, [isSaving, setIsSaving] = useState(false)
		, [isSavingAs, setIsSavingAs] = useState(false)
		, [versionInfo, setVersionInfo] = useState()
		, {
			searchTerm,
			detailsLoaded,
			items,
			versionVariables,
			creativeDetails,
			totalItems,
		} = useSelector((state) => {
			return {
				searchTerm: state.versionDetails.searchTerm,
				detailsLoaded: state.versionDetails.isLoaded,
				items: state.creativeVersions.items,
				totalItems: state.creativeVersions.totalItems,
				versionVariables: state.versionDetails.items,
				creativeDetails: state.creativeDetails,
			};
		})
		, selection = items?.find(ea => ea.id === parseInt(versionId))
		, title = versionInfo?.name || "Loading"
		//At Present, this check *does not* allow for DVAST because PRVW doesn't support that yet
		, shouldUseFrames = useFramingCheck(creativeDetails, formValues)
		;

	useEffect(() => {
		if (versionVariables && versionInfo?.variableConfig) {
			// Merge variableConfig into versioninfo for crop metadata
			versionVariables.forEach((variable) => {
				let matchedVariable = versionInfo.variableConfig[variable.name];
				if (matchedVariable && variable.cropMetadata) {
					matchedVariable.cropMetadata = variable.cropMetadata;
				}
			});

			setVersionInfo({ ...versionInfo, variableConfig: versionInfo.variableConfig });
		}
	}, [versionVariables]); //

	useEffect(() => {
		dispatch(loadVersionDetails(libraryId, parseInt(creativeId), parseInt(versionId)));
		if (!selection) {
			dispatch(fetchSingleVersion(libraryId, parseInt(creativeId), parseInt(versionId)))
				.then(resp => setVersionInfo(resp));
		}
		setVersionInfo(selection);
	}, [libraryId, creativeId, versionId, selection]);

	useEffect(() => {
		if (!versionVariables) { return; }

		const initValues = initializeEditVersionValues(versionVariables);

		dispatch(initialize(EDIT_VERSION_BLADE_FORM, initValues));
	}, [versionVariables]);

	const { search: searchAssets } = useSearchCache({
		key: "assets",
		urlParams: { libraryId },
		transform: getAssetOptionsFromPayload,
	});

	function closeModal() {
		dispatch(clearVersionDetails());
		dispatch(destroy(EDIT_VERSION_BLADE_FORM));
		// Clear crop metadata map for all variables
		dispatch(clearCropMetadata());
		onClose();
	}

	const confirmedClose = useConfirmClose(
		EDIT_VERSION_BLADE_FORM,
		closeModal,
		"Are you sure you want to cancel version editing? All data entered will be lost.",
		pristineSelector
	);

	function onToggleFullScreen() {
		setIsFullScreen(!isFullScreen);
	}

	const commonModalProps = {
		type: "blade",
		setAppTitle: false,

		xClose: true,
		allowFullScreen: true,
		isFullScreen,
		onToggleFullScreen,
	};

	const [, { pushModal }, ModalStackHead] = useModalStack({ commonModalProps });

	function saveVersion(values) {
		// formValues is a hash.  The edit endpoint wants the values as an array.
		setIsSaving(true);

		dispatch(updateVersionVariables(libraryId, creativeId, versionId, getVariableList(values, initialValues, libraryId)))
			.then((response) => {
				if (response?.success) {
					setIsSaving(false);
					closeModal();

					/*
						The update variables endpoint only returns the new variables, not the whole version.

						The version's modified date and size can potentially change, so we have to refetch
						to get the updated row data and respect the sorting and filtering.
					*/
					dispatch(loadCreativeVersions(libraryId, creativeId));
				}
			});
	}

	function saveAsVersion() {
		setIsSavingAs(true);
	}

	function previewVersion() {
		function Configured(props) {
			let modalProps = {
				...props,
				libraryId,
				versionInfo,
				saveVersion,
				saveAsVersion,
				pushModal,
			};
			return <EditVersionPreview {...modalProps} />;
		}

		setIsSaving(true);
		dispatch(updateVersionVariables(libraryId, creativeId, versionId, getVariableList(formValues, initialValues, libraryId), true))
			.then(({ success }) => {
				setIsSaving(false);
				if (success) {
					pushModal(Configured);
				}
			});
	}

	function previewInline(){
		function Configured(props) {
			let modalProps = {
				...props,
				libraryId,
				versionInfo,
				creative:creativeDetails,
				currentVariableInfo: serializeVariables(formValues, initialValues, libraryId),
				saveVersion,
				saveAsVersion,
				pushModal,
			};
			return <InlineVersionPreview {...modalProps} />;
		}

		pushModal(Configured);
	}

	const $saveAsModal = isSavingAs && <SaveAsModal {...{
		libraryId,
		creativeId,
		version: {
			...versionInfo,
			variableConfig: serializeVariables(formValues, initialValues, libraryId),
		},
		onClose() { setIsSavingAs(false); },
	}} />;

	function Footer() {
		let shouldDisableAllButtons = !hasEdits || isSaving;
		const hidePreviewButton = isAfterEffectsCreative(creativeDetails);

		let previewButton = null;

		if(shouldUseFrames){
			// Only disable this if the version is saving
			previewButton = (
				<Button
					className="cypress-preview-changes cypress-inline-preview-changes"
					disabled={isSaving}
					onClick={previewInline}>Preview Changes</Button>
			);
		} else {
			if(!hidePreviewButton){
				previewButton = (
					<Button
						disabled={shouldDisableAllButtons}
						className="cypress-preview-changes"
						onClick={previewVersion} >Preview Changes</Button>
				);
			}
		}

		return (
			<>
				<Button
					theme="default"
					className="cypress-cancel-edit-version"
					onClick={confirmedClose}>Cancel</Button>
				<div className="footer-actions">
					{previewButton}
					<Button
						disabled={shouldDisableAllButtons || isMaxDynamicSocialVersions(totalItems, creativeDetails)}
						className="cypress-version-save-as"
						onClick={saveAsVersion}>Save As...</Button>
					<Button
						disabled={shouldDisableAllButtons}
						className="cypress-save-version"
						theme="green"
						form={EDIT_VERSION_BLADE_FORM}
						type="submit"
					>Save Version</Button>
				</div>
			</>
		);
	}

	function Content() {
		return (
			<div className="main-content-container">
				<ProgressIndicator isLoading={!detailsLoaded || isSaving} />
				<Toolbar
					search
					searchTerm={searchTerm}
					handleSearch={newSearchTerm => dispatch(setVariableSearchTerm(newSearchTerm))}
					isLoading={!detailsLoaded || isSaving}
				/>
				<div className="main-content">
					<EditVersion
						searchAssets={searchAssets}
						libraryId={libraryId}
						saveVersion={saveVersion}
						saveAsVersion={saveAsVersion}
						versionInfo={versionInfo}
						onSave={saveVersion}
					/>
				</div>
			</div>
		);
	}

	let modalProps = {
		...commonModalProps,
		title: `Edit Version - ${title}`,
		className: "edit-version-blade",

		renderFooter: Footer,
		renderContent: Content,

		onClose: confirmedClose,
	};

	const blade = ModalStackHead ? <ModalStackHead versionInfo={versionInfo} creativeId={creativeId} /> : <Modal {...modalProps} />;

	return (
		<>
			{$saveAsModal}
			{blade}
		</>
	);
}
